import type {MatchParams} from '@wandb/weave/common/types/base';
import {useCallback} from 'react';

import {useDraft} from '../composer';
import type {Thread} from '../store';
import {queueKey, useQueueStore} from './store';
import {useTrackWBAgentQueue} from './tracking';

/**
 * useQueueRecall pulls a queued message back into the active thread's composer
 * draft for editing. `recall` prefills the draft from the item's parts,
 * restoring the ambient run chips as editable chips, and removes it from the
 * queue, so re-sending re-queues it. Returns whether it recalled; a refused
 * prefill (an occupied draft) or a missing item leaves the queue intact.
 */
export function useQueueRecall(
  activeThread: Thread | undefined,
  params: MatchParams
): (id: string) => boolean {
  const draft = useDraft(activeThread?.id ?? null);
  const track = useTrackWBAgentQueue(params, activeThread?.id);

  return useCallback(
    (id: string): boolean => {
      if (activeThread == null) {
        return false;
      }

      const key = queueKey(activeThread.id);
      const queue = useQueueStore.getState().queues[key] ?? [];
      const position = queue.findIndex(m => m.id === id);
      const item = queue[position];
      if (item == null) {
        return false;
      }

      if (!draft.setParts(item.content)) {
        return false;
      }

      track.trackRecalled(queue.length, position);
      useQueueStore.getState().remove(key, id);
      return true;
    },
    [activeThread, draft, track]
  );
}
